import { useState, useEffect, useCallback, useMemo } from "react";
import { PaginatedResult } from "../types/api";
import { addUniqueItems, deduplicateItems } from "../utils/assignmentUtils";
import { useInfiniteScroll } from "./useInfiniteScroll";

/** The slice of a useEntitySearch result the assignment lists read. */
export interface AssignmentSearchSource<T> {
  results: T[];
  loading: boolean;
  loadingMore: boolean;
  hasSearched: boolean;
  total: number;
  hasMore: boolean;
  loadMore: () => void;
}

interface UseAssignmentListsOptions<T> {
  isOpen: boolean;
  /** Every assigned item. The assigned side is small enough to hold whole. */
  loadAssigned: () => Promise<T[] | null>;
  /** One page of the full catalogue, assigned items included. */
  loadAvailablePage: (page: number) => Promise<PaginatedResult<T> | null>;
  /** Omit for a modal without a search box. */
  search?: AssignmentSearchSource<T>;
  errorMessage: string;
}

const NO_SEARCH: AssignmentSearchSource<never> = {
  results: [],
  loading: false,
  loadingMore: false,
  hasSearched: false,
  total: 0,
  hasMore: false,
  loadMore: () => {},
};

/**
 * The two columns of an assignment modal: the complete assigned list held
 * in memory, and an available list that pages through the catalogue (or
 * the search results) minus whatever is assigned. Assigning or unassigning
 * moves an item between the two arrays; nothing is refetched.
 *
 * Both loaders must be memoised by the caller; each re-runs when its
 * identity changes while the modal is open.
 */
export const useAssignmentLists = <T extends { id: number }>({
  isOpen,
  loadAssigned,
  loadAvailablePage,
  search = NO_SEARCH,
  errorMessage,
}: UseAssignmentListsOptions<T>) => {
  const [assigned, setAssigned] = useState<T[]>([]);
  const [assignedLoading, setAssignedLoading] = useState(false);

  const [loadedItems, setLoadedItems] = useState<T[]>([]);
  const [loadedTotal, setLoadedTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    setAssignedLoading(true);
    loadAssigned()
      .then((items) => {
        if (cancelled) return;
        if (items) {
          setAssigned(deduplicateItems(items, (item) => item.id));
        } else {
          setError(errorMessage);
        }
      })
      .catch(() => {
        if (!cancelled) setError(errorMessage);
      })
      .finally(() => {
        if (!cancelled) setAssignedLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, loadAssigned, errorMessage]);

  const fetchPage = useCallback(
    async (pageNumber: number, append: boolean) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setPageLoading(true);
      }
      setError(null);

      try {
        const result = await loadAvailablePage(pageNumber);
        if (!result) {
          setError(errorMessage);
          return;
        }
        setLoadedItems((prev) =>
          append
            ? addUniqueItems(prev, result.items, (item) => item.id)
            : result.items
        );
        setLoadedTotal(result.total);
        setPage(result.page);
        setHasMore(result.page < result.lastPage);
      } catch (err) {
        setError(errorMessage);
      } finally {
        setPageLoading(false);
        setLoadingMore(false);
      }
    },
    [loadAvailablePage, errorMessage]
  );

  useEffect(() => {
    if (isOpen) {
      fetchPage(1, false);
    }
  }, [isOpen, fetchPage]);

  const assignedIds = useMemo(
    () => new Set(assigned.map((item) => item.id)),
    [assigned]
  );

  const available = useMemo(() => {
    const source = search.hasSearched ? search.results : loadedItems;
    return source.filter((item) => !assignedIds.has(item.id));
  }, [search.hasSearched, search.results, loadedItems, assignedIds]);

  // Search totals include assigned rows; subtract the hidden ones.
  const availableTotal = search.hasSearched
    ? Math.max(0, search.total - (search.results.length - available.length))
    : Math.max(0, loadedTotal - assigned.length);

  const markAssigned = useCallback((item: T) => {
    setAssigned((prev) => addUniqueItems(prev, [item], (i) => i.id));
  }, []);

  // Also added to the loaded catalogue, so it is visible even if its page
  // has not been scrolled to.
  const markUnassigned = useCallback((item: T) => {
    setAssigned((prev) => prev.filter((i) => i.id !== item.id));
    setLoadedItems((prev) => addUniqueItems(prev, [item], (i) => i.id));
  }, []);

  const loadMorePages = () => {
    if (hasMore && !pageLoading && !loadingMore) {
      fetchPage(page + 1, true);
    }
  };

  const { handleScroll: handleAvailableScroll } = useInfiniteScroll({
    hasMore: search.hasSearched ? search.hasMore : hasMore,
    onLoadMore: search.hasSearched ? search.loadMore : loadMorePages,
    isLoading: search.hasSearched
      ? search.loading || search.loadingMore
      : pageLoading || loadingMore,
    threshold: 100,
  });

  return {
    assigned,
    available,
    availableTotal,
    assignedLoading,
    availableLoading: pageLoading || search.loading,
    loadingMore: search.hasSearched ? search.loadingMore : loadingMore,
    error,
    handleAvailableScroll,
    markAssigned,
    markUnassigned,
  };
};
